import { useEffect, useState } from 'react';
import BordSelectDropdown from 'components/NudosComponents/BordSelectDropdown/BordSelectDropdown';
import OpenLogisticServiceReportPanel from '../OpenLogisticServiceReportPanel/OpenLogisticServiceReportPanel';
import { NudosButton, NudosSearchbarDropdown } from '../NudosComponents';
import { IconWarning } from '../../assets/DesignSystem/SVGComponents';
import { getLogisticServiceReportReasons, postReportLogisticServiceProblem } from '../../services/services';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import {
  IReportLogisticServiceReason,
  IlogisticServiceDetails,
  REPORT_LOGISTIC_SERVICE_REASON_GLOBAL_STATUS,
} from '../../types/services';
import { getNudosData } from '../../utils/getNudosData';
import ReportReasonsExampleModal from './ReportReasonsExampleModal/ReportReasonsExampleModal';
import useServiceState from '../../state/useServicesState';
import { getLatestOpenLogisticReport } from 'views/LogisticServices/utils/general.utils';
import { LOGISTIC_SERVICE_STATUS_NAME } from 'types/suscription/history.types';
import EmptyReportLogisticServiceProblem from './Components/EmptyReportLogisticServiceProblem';
import './NudosReportLogisticServiceProblem.scss';

interface INudosReportLogisticServiceProblemProps {
  customClassname?: string;
  afterSubmitCallback?: () => void;
  displayAttemptsSelect?: boolean;
  logisticServiceDetails?: IlogisticServiceDetails;
  displayReportLSProblem?: boolean;
  autoOpenDropdown?: boolean;
  hideOpenReportPanel?: boolean;
}

const NudosReportLogisticServiceProblem = ({
  customClassname,
  afterSubmitCallback,
  displayAttemptsSelect,
  logisticServiceDetails,
  displayReportLSProblem,
  autoOpenDropdown = false,
  hideOpenReportPanel = false
}: INudosReportLogisticServiceProblemProps) => {
  const nudosData = getNudosData();
  const { setReportReason, setReasonAttempt, reasonAttempt } = useServiceState();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [reportReasonsList, setReportReasonsList] = useState<IReportLogisticServiceReason[]>();
  const [reportProblemLoader, setReportProblemLoader] = useState<boolean>(false);
  const [reportReasonsLoading, setReportReasonsLoading] = useState<boolean>(false);
  const [reportReasonSelected, setReportReasonSelected] = useState<IReportLogisticServiceReason>();
  const [openReportReasonModal, setOpenReportReasonModal] = useState<number>(0);

  const logisticStatusName = logisticServiceDetails?.statusName;
  const reportNumberOfTries = logisticServiceDetails?.reportNumberOfTries || 0;
  const latestOpenReport = getLatestOpenLogisticReport(logisticServiceDetails?.reports);
  const hasOpenReports = latestOpenReport != null;
  const openReportsForPanel = latestOpenReport ? [latestOpenReport] : [];
  const logisticServiceId = logisticServiceDetails?.id;
  const showCloseReportPanel = !hideOpenReportPanel;
  const shouldRenderOpenReportPanel =
    hasOpenReports && logisticServiceId != null && showCloseReportPanel;

  const filteredReasonsList = reportReasonsList?.filter(reasons => {
    if (
      logisticStatusName === LOGISTIC_SERVICE_STATUS_NAME.IN_PROCESS ||
      logisticStatusName === LOGISTIC_SERVICE_STATUS_NAME.CONFIRMED
    ) {
      return reasons?.globalStatus === REPORT_LOGISTIC_SERVICE_REASON_GLOBAL_STATUS.IN_PROGRESS;
    }

    if (logisticStatusName === LOGISTIC_SERVICE_STATUS_NAME.ACTIVE) {
      return reasons?.globalStatus === REPORT_LOGISTIC_SERVICE_REASON_GLOBAL_STATUS.DELAY_ACTIVE;
    }

    return reasons?.globalStatus === REPORT_LOGISTIC_SERVICE_REASON_GLOBAL_STATUS.OTHER;
  });

  const displayLoader = !reportReasonsList || reportReasonsLoading || !filteredReasonsList;
  const saveButtonDisabled =
    !reportReasonSelected || reportProblemLoader || (displayAttemptsSelect && !reasonAttempt);
  const reportWithAttempt = [6, 7, 8, 9]?.includes(reportReasonSelected?.id || 0);
  const nudosSelectDropdownDisabled = !reportReasonSelected || reportProblemLoader || reportWithAttempt;

  const cleanGlobalStates = () => {
    setReportReason(undefined);
    setReasonAttempt(undefined);
  };

  const formDropdownOptionsCallback = (
    rawOptions: IReportLogisticServiceReason[],
    clickOptionCallback?: (option: IReportLogisticServiceReason) => void
  ): JSX.Element[] => {
    const handleClick = (rawOption: IReportLogisticServiceReason) => {
      setReportReasonSelected(rawOption);
      setReportReason(rawOption);
      setReasonAttempt(undefined);
      clickOptionCallback && clickOptionCallback(rawOption);
    };

    const categorizeOptions = (
      options: IReportLogisticServiceReason[],
      categoryType: string,
      categoryTitle: string
    ) => {
      const filteredOptions = options.filter(option => option.reasonType === categoryType);
      return filteredOptions.length > 0
        ? [
          <div key={`${categoryTitle}`} className="optionTitle">
            {categoryTitle}
          </div>,
          ...renderOptions(filteredOptions)
        ]
        : [];
    };

    const renderOptions = (options: IReportLogisticServiceReason[]): JSX.Element[] => {
      return options.map((rawOption, i) => (
        <div key={`option-${rawOption?.id}-${i}`} className="optionContainer">
          <div className="optionLine">
            <div className="optionValue" onClick={() => handleClick(rawOption)}>
              {rawOption?.nameString}
            </div>
            <div className="example" onClick={() => setOpenReportReasonModal(rawOption?.id || 0)}>
              Ver ejemplo
            </div>
          </div>
        </div>
      ));
    };

    const internalOptions = categorizeOptions(rawOptions, 'INTERNAL', 'Razones internas');
    const externalOptions = categorizeOptions(rawOptions, 'EXTERNAL', 'Razones externas');
    const pushResponseOptions = categorizeOptions(rawOptions, 'PUSH_RESPONSE', 'Push para respuesta');
    const generalOptions = categorizeOptions(rawOptions, 'GENERAL', 'Correos generales');
    const collectDeliveryOptions = categorizeOptions(
      rawOptions,
      'COLLECT_DELIVERY',
      'Con intentos de recolección/entrega'
    );
    return [
      ...internalOptions,
      ...externalOptions,
      ...pushResponseOptions,
      ...generalOptions,
      ...collectDeliveryOptions
    ];
  };

  const formSelectedOptionUICallback = (rawOption: IReportLogisticServiceReason) => {
    return <div className="optionContainer">{rawOption?.nameString}</div>;
  };

  const filterBySearchStringCallback = (search: string, rawOptions: IReportLogisticServiceReason[]) => {
    return rawOptions.filter(rawOption =>
      rawOption?.nameString?.toLowerCase().trim().includes(search?.toLowerCase().trim())
    );
  };

  const getReportReasons = async () => {
    setReportReasonsLoading(true);
    try {
      const reportReasonData = await getLogisticServiceReportReasons();
      setReportReasonsList(reportReasonData);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setReportReasonsLoading(false);
    }
  };

  const reportLogisticServiceProblem = async () => {
    if (!logisticServiceDetails) return;
    setReportProblemLoader(true);
    try {
      const body = {
        userId: nudosData?.userData?.id,
        reasonId: reportReasonSelected?.id,
        logisticServiceId: logisticServiceDetails?.id,
        numberOfTry: reasonAttempt?.value || undefined
      };
      await postReportLogisticServiceProblem(body);
      displaySuccessNotification({ customJSXMessage: <>Reporte del problema con este servicio enviado con éxito.</> });
      afterSubmitCallback && afterSubmitCallback();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setReportProblemLoader(false);
    }
  };

  const formAttemptOptions = () => {
    return [
      { value: 1, label: 'Primer intento fallido' },
      { value: 2, label: 'Segundo intento fallido' },
      { value: 3, label: 'Tercer y último intento - se cancela SL' }
    ].map((val, i) => {
      const disabledOption = reportNumberOfTries !== val.value - 1 ? 'disabledOption' : '';
      const onclickAction = () => !disabledOption && setReasonAttempt(val);
      return (
        <div key={`attempOption${i}`} className={`optionContainer ${disabledOption}`} onClick={onclickAction}>
          {val?.label}
        </div>
      );
    });
  };

  useEffect(() => {
    getReportReasons();
    return cleanGlobalStates();
  }, []);

  useEffect(() => {
    if (autoOpenDropdown) {
      setTimeout(() => {
        setIsDropdownOpen(true);
      }, 100);
    }
  }, [autoOpenDropdown]);

  if (!displayReportLSProblem && !(hasOpenReports && showCloseReportPanel)) {
    return <EmptyReportLogisticServiceProblem />;
  }

  return (
    <div className={`nudosReportLogisticServiceProblem ${customClassname || ''}`}>
      {!!openReportReasonModal && (
        <ReportReasonsExampleModal
          reasonId={openReportReasonModal}
          closeModalCallback={() => setOpenReportReasonModal(0)}
        />
      )}
      {shouldRenderOpenReportPanel && (
        <OpenLogisticServiceReportPanel
          openReports={openReportsForPanel}
          logisticServiceId={logisticServiceId}
          onStatusUpdated={() => afterSubmitCallback?.()}
        />
      )}
      {displayReportLSProblem && (
        <div className="nudosReportLogisticServiceProblemContainer">
          {!displayAttemptsSelect && (
            <div className="nudosReportLSTitle">
              Reportar un problema en el servicio
              <IconWarning className="IconWarning" />
            </div>
          )}
          <div className="informationText">
            Informa al cliente sobre problemas en el servicio que estén confirmados o en proceso. Esta información se
            comunicará al cliente a través de un correo electrónico.
          </div>
          <div className="flex items-end">
            <div>
              <NudosSearchbarDropdown
                customClassname="nudosReportLSSearchbarDropdown"
                label="Escoge la razón del problema "
                isFilled={!!reportReasonSelected}
                rawOptions={filteredReasonsList || []}
                currentValue={reportReasonSelected}
                showSkeletonLoader={displayLoader}
                componentSize="medium"
                isDeactivated={reportProblemLoader}
                customPlaceholder="Escoge la razón del problema "
                includeMagnifierIcon={false}
                formDropdownOptionsCallback={formDropdownOptionsCallback}
                formSelectedOptionUICallback={formSelectedOptionUICallback}
                filterBySearchStringCallback={filterBySearchStringCallback}
                openDropdown={isDropdownOpen}
              />
              <div className="footerText">Esta razón se le comunicará al cliente por medio de un correo electrónico</div>
            </div>
            {displayAttemptsSelect && (
              <BordSelectDropdown
                label="Número de intento"
                isFilled={!!reasonAttempt}
                isDeactivated={nudosSelectDropdownDisabled}
                selectOptions={formAttemptOptions()}
                selectClassName="attemptsSelect"
                currentValueOrplaceholder={reasonAttempt?.label || 'Escoge el número de intento'}
              />
            )}
            {!displayAttemptsSelect && (
              <NudosButton
                buttonText="Guardar"
                customWidth="69px"
                handleClick={reportLogisticServiceProblem}
                customHeight="28px"
                customClassName="saveButton"
                isButtonDisabled={saveButtonDisabled}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NudosReportLogisticServiceProblem;
